import RequestHeadersBase from '~/common/constants/RequestHeadersBase';
import type { ConfirmPassword } from '~/domains/auth/interfaces/confirmPassword.interface';
import type { ForgotPassword } from '~/domains/auth/interfaces/forgotPassword.interface';
import type { EO_BRANDS, PLATFORMS } from '~/domains/auth/consts';
import type { ApplicationTypeValues } from '~/domains/auth/types/application-type';

import type { APIAuthUserResponse } from '../types/login.type';

import { identityInstance } from './identity-instance';

const forgotPassword = async (input: ForgotPassword) =>
  identityInstance.post('/universal-auth/forgot-password', input);

const confirmPassword = async (input: ConfirmPassword) =>
  identityInstance.post('/universal-auth/confirm-password', input);

interface LogInInput {
  email: string;
  password: string;
  token?: string;
  destination?: keyof typeof EO_BRANDS;
  platform?: PLATFORMS;
}

interface APILogInResponse {
  id: string; // user ID
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  singleUseToken: {
    token: string;
    expiresAt: Date;
  };
}

const logIn = async (input: LogInInput): Promise<APILogInResponse> => {
  const response = await identityInstance.post('/universal-auth/login', input);

  return response.data;
};

interface SignUpInput {
  handle: string;
  name?: string;
  email: string;
  password: string;
  referralCode?: string;
  referral?: string;
  token?: string;
  firstName?: string;
  lastName?: string;
  destination?: keyof typeof EO_BRANDS;
  platform?: PLATFORMS;
}

const signUp = async (input: SignUpInput) => {
  const response = await identityInstance.post('/universal-auth/register', input);

  return response.data;
};

interface OAuthSignInInput {
  applicationType: ApplicationTypeValues;
  authorizationCode: string;
  createChannel?: boolean;
}

const oauthSignIn = async (input: OAuthSignInInput): Promise<APIAuthUserResponse> => {
  const response = await identityInstance.post('/oauth/login', input);

  return response.data;
};

interface OAuthSignUpInput {
  handle: string;
  email: string;
  referralCode?: string;
  applicationType: string;
  authorizationCode: string;
  createChannel?: boolean;
  referral?: string;
}

const oauthSignUp = async (input: OAuthSignUpInput) => {
  const response = await identityInstance.post('/oauth/register', input);

  return response.data;
};

export const exchangeSingleUseTokenForAccessToken = async (singleUseToken: string) => {
  const response = await identityInstance.post(`/universal-auth/single-use-tokens`, undefined, {
    headers: {
      ...RequestHeadersBase,
      Authorization: `Bearer ${singleUseToken}`,
      'x-api-key': process.env.SINGLE_USE_AUTH_API_KEY,
    },
  });

  return response.data;
};

export const getReferralCode = async (referralCode: string) => {
  const response = await identityInstance.get(`/referral-codes/${referralCode}`);

  return response.data;
};

export { confirmPassword, forgotPassword, logIn, oauthSignIn, oauthSignUp, signUp };

export type { LogInInput };
